import { useQueryClient } from "@tanstack/react-query";
import { useCallback, type ReactNode } from "react";

import { preloadDocumentEditor } from "../knowledge/preload-editor";
import { customerDetailOptions } from "./customer-queries";
import { parseCustomerRoute } from "./customer-route";
import { docsBootstrapOptions } from "./docs-queries";

import { OperationsPrefetchContext } from "./operations-prefetch-context";

function shouldPrefetch() {
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  return (
    navigator.onLine &&
    !connection?.saveData &&
    !["slow-2g", "2g"].includes(connection?.effectiveType ?? "")
  );
}

export function OperationsPrefetchProvider({
  children,
  canReadDocs,
}: {
  children: ReactNode;
  canReadDocs: boolean;
}) {
  const client = useQueryClient();
  const prefetch = useCallback(
    (href: string) => {
      if (!shouldPrefetch()) return;
      const target = new URL(href, window.location.origin);
      if (target.origin !== window.location.origin || target.pathname === window.location.pathname)
        return;
      if (canReadDocs && (target.pathname === "/docs" || target.pathname.startsWith("/docs/"))) {
        void preloadDocumentEditor();
        // Never prefetch audited document bodies. Navigation performs the actual, authorized read.
        const options = docsBootstrapOptions();
        if (!client.getQueryState(options.queryKey))
          void client.prefetchQuery({ ...options, retry: false });
      }
      const customer = parseCustomerRoute(target.pathname);
      if (customer) {
        // Only the lightweight summary, never audit, billing, files or support actions.
        const options = customerDetailOptions(customer.slug);
        if (!client.getQueryState(options.queryKey))
          void client.prefetchQuery({ ...options, retry: false });
      }
    },
    [client, canReadDocs],
  );
  return <OperationsPrefetchContext value={prefetch}>{children}</OperationsPrefetchContext>;
}
